import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { UserCog } from "lucide-react";
import { exitImpersonation } from "../services/user.service";
import { getApiErrorMessage } from "../lib/apiClient";
import type { AuthUser } from "../types";
import type { ImpersonatorInfo } from "../services/auth.service";

const ROLE_LABELS: Record<AuthUser["role"], string> = {
  SUPER_ADMIN: "Super Admin",
  EVENT_MANAGER: "Event Manager",
  EVENT_STAFF: "Event Staff",
};

export function ImpersonationBanner({
  user,
  impersonatedBy,
}: {
  user: AuthUser;
  impersonatedBy: ImpersonatorInfo;
}): JSX.Element {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: exitImpersonation,
    onSuccess: async (adminUser) => {
      // Every other cached query (events, users, etc.) was fetched as the
      // impersonated user and is no longer valid for the admin's session.
      // Seed auth/me with the now-known admin identity first (avoids a
      // flash of "logged out" while the refetch below is in flight), then
      // clear everything else so those components refetch fresh under the
      // restored identity.
      queryClient.setQueryData(["auth", "me"], { user: adminUser, impersonatedBy: null });
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
      toast.success("Exited impersonation");
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  return (
    <div className="relative z-40 flex items-center justify-center gap-3 bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 text-sm font-medium text-white shadow-md">
      <UserCog className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        {impersonatedBy.name} is viewing as <strong>{user.name}</strong> ({ROLE_LABELS[user.role]})
      </span>
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="rounded-md bg-white/20 px-3 py-1 text-xs font-semibold transition-colors hover:bg-white/30 disabled:opacity-50"
      >
        {mutation.isPending ? "Exiting..." : "Exit impersonation"}
      </button>
    </div>
  );
}
